import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";

const THEME_KEY = "hookora_theme";

const WaitlistAdmin = () => {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [error, setError] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  useEffect(() => {
    API.get("/waitlist/admin/leads")
      .then((res) => {
        setLeads(res.data.leads || []);
        setCount(res.data.count || 0);
        setUserAnalytics(res.data.user_analytics || null);
        setError("");
      })
      .catch((err) => {
        console.error("Failed to load waitlist leads:", err);
        setError(
          err?.response?.data?.detail || "Failed to load admin waitlist view."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      tableHeadBg: isDark ? "rgba(30,41,59,0.92)" : "#f9fafb",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderSoft: isDark ? "rgba(148,163,184,0.12)" : "#f1f5f9",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c"
    }),
    [isDark]
  );

  const statCardStyle = {
    background: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: "18px",
    padding: "18px",
    boxShadow: colors.shadow,
    backdropFilter: isDark ? "blur(14px)" : "none"
  };

  const formatPlanLabel = (plan) => {
    const normalized = (plan || "free").toLowerCase();
    if (normalized === "creator") return "✔ Creator";
    if (normalized === "pro") return "✔ Pro";
    return "Free";
  };

  const planBadgeStyle = (plan) => {
    const normalized = (plan || "free").toLowerCase();

    if (normalized === "creator") {
      return {
        color: "#1d4ed8",
        background: isDark ? "rgba(29,78,216,0.18)" : "#dbeafe",
        border: `1px solid ${isDark ? "rgba(147,197,253,0.34)" : "#93c5fd"}`
      };
    }

    if (normalized === "pro") {
      return {
        color: isDark ? "#d8b4fe" : "#6d28d9",
        background: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
        border: `1px solid ${isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe"}`
      };
    }

    return {
      color: isDark ? "#fbbf24" : "#92400e",
      background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`
    };
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "40px",
          color: colors.text,
          background: colors.pageBg,
          minHeight: "100vh"
        }}
      >
        Loading admin analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "40px",
          background: colors.pageBg,
          minHeight: "100vh"
        }}
      >
        <div
          style={{
            background: colors.errorBg,
            border: `1px solid ${colors.errorBorder}`,
            color: colors.errorText,
            borderRadius: "14px",
            padding: "16px"
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: colors.pageBg,
        minHeight: "100vh",
        padding: isMobile ? "24px 14px" : "32px 20px"
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? "28px" : "34px" }}>
          Waitlist Admin
        </h1>
        <p style={{ color: colors.textMuted, marginTop: "10px", lineHeight: "1.6" }}>
          Review growth, signups, paid conversions, and billing-interest leads from one admin view.
        </p>

        {userAnalytics && (
          <>
            <div style={{ marginTop: "24px" }}>
              <h2 style={{ color: colors.text, marginBottom: "14px" }}>User Growth Overview</h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "16px"
                }}
              >
                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Total Registered Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.total_users || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>New Users (7 Days)</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.new_users_7d || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>New Users (30 Days)</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.new_users_30d || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Active Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.active_users || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Paid Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.paid_users || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Signup to Paid Conversion</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.signup_to_paid_conversion_rate || 0}%
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "24px" }}>
              <h2 style={{ color: colors.text, marginBottom: "14px" }}>Plan Breakdown</h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "16px"
                }}
              >
                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Free Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.free_users || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Creator Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.creator_users || 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div style={{ color: colors.textMuted, fontSize: "13px" }}>Pro Users</div>
                  <div style={{ marginTop: "8px", fontSize: "30px", fontWeight: "800", color: colors.text }}>
                    {userAnalytics.pro_users || 0}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "24px" }}>
              <h2 style={{ color: colors.text, marginBottom: "14px" }}>Recent Signups</h2>

              {isMobile ? (
                <div style={{ display: "grid", gap: "14px" }}>
                  {userAnalytics.recent_signups?.length ? (
                    userAnalytics.recent_signups.map((user) => (
                      <div
                        key={user.id}
                        style={{
                          ...statCardStyle,
                          padding: "16px"
                        }}
                      >
                        <div style={{ display: "grid", gap: "10px" }}>
                          <div>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                              Username
                            </div>
                            <div style={{ marginTop: "4px", color: colors.text, fontWeight: "700" }}>
                              {user.username}
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                              Email
                            </div>
                            <div style={{ marginTop: "4px", color: colors.textSoft, wordBreak: "break-word" }}>
                              {user.email}
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                              Plan
                            </div>
                            <div style={{ marginTop: "6px" }}>
                              <span
                                style={{
                                  ...planBadgeStyle(user.plan),
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  padding: "6px 10px",
                                  borderRadius: "999px"
                                }}
                              >
                                {formatPlanLabel(user.plan)}
                              </span>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                              Billing Status
                            </div>
                            <div style={{ marginTop: "4px", color: colors.textSoft }}>
                              {user.billing_status || "inactive"}
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                              Created At
                            </div>
                            <div style={{ marginTop: "4px", color: colors.textSoft }}>
                              {user.created_at ? new Date(user.created_at).toLocaleString() : "—"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={statCardStyle}>No recent signups yet.</div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    background: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: colors.shadow,
                    backdropFilter: isDark ? "blur(14px)" : "none"
                  }}
                >
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                      <thead>
                        <tr style={{ background: colors.tableHeadBg, textAlign: "left" }}>
                          <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Username</th>
                          <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Email</th>
                          <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Plan</th>
                          <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Billing Status</th>
                          <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userAnalytics.recent_signups?.length ? (
                          userAnalytics.recent_signups.map((user) => (
                            <tr key={user.id}>
                              <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.text }}>
                                {user.username}
                              </td>
                              <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                                {user.email}
                              </td>
                              <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}` }}>
                                <span
                                  style={{
                                    ...planBadgeStyle(user.plan),
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    padding: "6px 10px",
                                    borderRadius: "999px"
                                  }}
                                >
                                  {formatPlanLabel(user.plan)}
                                </span>
                              </td>
                              <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                                {user.billing_status || "inactive"}
                              </td>
                              <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                                {user.created_at ? new Date(user.created_at).toLocaleString() : "—"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              style={{
                                padding: "20px",
                                textAlign: "center",
                                color: colors.textMuted
                              }}
                            >
                              No recent signups yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: "28px" }}>
          <h2 style={{ color: colors.text, marginBottom: "14px" }}>Billing Interest Leads</h2>
        </div>

        <div
          style={{
            marginTop: "0",
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            padding: "18px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <strong style={{ color: colors.text }}>Total leads:</strong>{" "}
          <span style={{ color: colors.textSoft }}>{count}</span>
        </div>

        {isMobile ? (
          <div style={{ marginTop: "20px", display: "grid", gap: "14px" }}>
            {leads.length > 0 ? (
              leads.map((lead) => (
                <div
                  key={lead.id}
                  style={{
                    background: colors.cardBgStrong,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "18px",
                    padding: "16px",
                    boxShadow: colors.shadow,
                    backdropFilter: isDark ? "blur(14px)" : "none"
                  }}
                >
                  <div style={{ display: "grid", gap: "10px" }}>
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Email
                      </div>
                      <div style={{ color: colors.text, marginTop: "4px", wordBreak: "break-word" }}>
                        {lead.email}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Username
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px" }}>
                        {lead.username || "—"}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Current Plan
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px" }}>
                        {lead.current_plan || "—"}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Interested In
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px" }}>
                        {lead.interested_in || "—"}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Notes
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px", lineHeight: "1.6" }}>
                        {lead.notes || "—"}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Created At
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px" }}>
                        {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: colors.textMuted, textTransform: "uppercase" }}>
                        Contacted
                      </div>
                      <div style={{ color: colors.textSoft, marginTop: "4px" }}>
                        {lead.contacted ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "18px",
                  padding: "20px",
                  textAlign: "center",
                  color: colors.textMuted,
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none"
                }}
              >
                No waitlist leads yet.
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              marginTop: "20px",
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none"
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                <thead>
                  <tr style={{ background: colors.tableHeadBg, textAlign: "left" }}>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Email</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Username</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Current Plan</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Interested In</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Notes</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Created At</th>
                    <th style={{ padding: "14px", borderBottom: `1px solid ${colors.border}` }}>Contacted</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length > 0 ? (
                    leads.map((lead) => (
                      <tr key={lead.id}>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.text }}>
                          {lead.email}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                          {lead.username || "—"}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                          {lead.current_plan || "—"}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                          {lead.interested_in || "—"}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft, maxWidth: "260px" }}>
                          {lead.notes || "—"}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                          {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
                        </td>
                        <td style={{ padding: "14px", borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textSoft }}>
                          {lead.contacted ? "Yes" : "No"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: colors.textMuted
                        }}
                      >
                        No waitlist leads yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistAdmin;